import { sortOptions } from '../../types';

import { useBidFilterStore } from '../useBidFilterStore';

interface IUsePrepareBidsListQueryPayload {
  count?: number;
  pageNumber: number;
}

interface IUsePrepareBidsListQueryPayloadOptions {
  fromDate?: string;
  groupIds?: string[];
  toDate?: string;
}

interface IBidsSearchQueryUrlParams
  extends IUsePrepareBidsListQueryPayloadOptions {
  isHotDeal?: boolean;
}

/**
 * This count will define how many items we needed for each api fetch.
 */
export const ITEM_COUNT = 50;

/**
 * This abstracts away the payload preparation for the bids list query since it
 * takes in a lot of keys that are easy to miss.
 *
 * This will also allow for reuse of this hook by other components outside this
 * navigation tree.
 */
export function usePrepareBidsListQueryPayload(
  options: IUsePrepareBidsListQueryPayload
) {
  const { count = ITEM_COUNT, pageNumber } = options;

  const {
    bidListSortFilterIndex,
    bidSearchKey,
    contextId,
    dateRange,
    selectedAssetGroups,
    selectedBidRankFilter,
    selectedBidsTabGroup,
    selectedBrandBidFilterList,
    selectedCategoryBidFilter,

    sortBy: tableSortBy,
    orderBy: tableOrderBy,
  } = useBidFilterStore();

  const { startDate, endDate } = dateRange || {};
  let orderBy = tableOrderBy || '';
  const sortBy = tableSortBy;

  const groupIds =
    selectedAssetGroups?.map((ag) => ag.value).filter(Boolean) || [];

  const isHotDeal = selectedBidsTabGroup === 'HOT_DEALS';

  if (!tableOrderBy && !isHotDeal) {
    /**
     * For hot deals, No bids and non selected rank list don't have `orderBy`
     * filter
     */
    const isOrderByActive =
      selectedBidRankFilter !== 'NO_BIDS' ||
      selectedBidRankFilter !== undefined;

    if (isOrderByActive) {
      orderBy = sortOptions[bidListSortFilterIndex] || 'DESCENDING';
    }
  }

  const urlParams: IBidsSearchQueryUrlParams = {
    isHotDeal,
    fromDate: startDate ?? undefined,
    toDate: endDate ?? undefined,
    ...(groupIds.length > 0 ? { groupIds } : {}),
  };

  const brandListItemIds = selectedBrandBidFilterList.map((brand) => brand.id);

  const contextIdValue = contextId?.contextId || '';
  const contextIdSearchKey = contextId?.searchKey || '';

  /**
   * If contextId is present and it is store regarding the same search key as
   * `bidSearchKey` then we need to add it to the payload.
   */
  if (contextId && bidSearchKey === contextIdSearchKey) {
    const payload = {
      bidFilterKey: selectedBidRankFilter,
      brandList: brandListItemIds,
      categoryId: selectedCategoryBidFilter?.id,
      count,
      orderBy,
      page: pageNumber,
      search: bidSearchKey,
      contextId: contextIdValue,
      ...(sortBy ? { sortBy } : {}),
    };

    return {
      count,
      payload,
      urlParams,
    };
  }

  const payload = {
    bidFilterKey: selectedBidRankFilter,
    brandList: brandListItemIds,
    categoryId: selectedCategoryBidFilter?.id,
    count,
    orderBy,
    page: pageNumber,
    search: bidSearchKey,
    ...(sortBy ? { sortBy } : {}),
  };

  // TODO: Why is count assigned and reassigned in multiple places here yet there
  // its not changed at all?
  return {
    count,
    payload,
    urlParams,
  };
}
